import type { Tweet } from "@podx/core/types";

export interface TokenMention {
  symbol: string;
  count: number;
  tweets: Tweet[];
  sentiment: 'positive' | 'negative' | 'neutral';
  sentimentScore: number; // -1 to 1 scale
  avgEngagement: number;
  firstSeen: Date;
  lastSeen: Date;
  uniqueAccounts: number;
  botScore: number; // 0-1, higher = more likely bot
  shillProbability: number; // 0-1, higher = more likely shilling
}

export interface AccountProfile {
  username: string;
  tweetCount: number;
  tokensPosted: string[];
  avgEngagement: number;
  followerCount?: number;
  accountAge?: number;
  botScore: number;
  shillScore: number;
  reputation: 'trusted' | 'suspicious' | 'bot';
}

export class TokenExtractor {
  private tokenPattern = /\$([A-Z]{2,10}|[A-Z][A-Z0-9]{1,9})/g;

  // Known monetary patterns to exclude
  private monetaryPatterns = [
    /^\$\d+[KMGTPEZY]?/,  // $50, $10MM, $750K, etc.
    /^\$\d+\.\d+/,        // $50.00, etc.
    /^\$\d+[MB]illion/,   // $1Million, $2Billion, etc.
    /^\$\d+[KMB]/,        // $5K, $10M, $1B, etc.
  ];

  // Common monetary abbreviations to exclude
  private monetaryTerms = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'CHF', 'JPY', 'CNY'];

  extractTokenMentions(tweets: Tweet[]): TokenMention[] {
    const tokenMap = new Map<string, Tweet[]>();
    const accountMap = new Map<string, AccountProfile>();

    // Group tweets by token mentions and build account profiles
    for (const tweet of tweets) {
      const tokens = tweet.text.match(this.tokenPattern) || [];
      const username = tweet.username || 'unknown';

      // Build account profile
      if (!accountMap.has(username)) {
        accountMap.set(username, {
          username,
          tweetCount: 0,
          tokensPosted: [],
          avgEngagement: 0,
          botScore: 0,
          shillScore: 0,
          reputation: 'trusted'
        });
      }

      const account = accountMap.get(username)!;
      account.tweetCount++;

      for (const token of tokens) {
        const cleanToken = token.toUpperCase();

        // Filter out monetary amounts
        if (this.isMonetaryAmount(cleanToken)) {
          continue;
        }

        // Add to token map
        if (!tokenMap.has(cleanToken)) {
          tokenMap.set(cleanToken, []);
        }
        tokenMap.get(cleanToken)!.push(tweet);

        // Add to account's token list
        if (!account.tokensPosted.includes(cleanToken)) {
          account.tokensPosted.push(cleanToken);
        }
      }
    }

    // Convert to TokenMention objects
    const mentions: TokenMention[] = [];
    for (const [symbol, tweets] of tokenMap) {
      const engagement = tweets.reduce((sum, t) => sum + t.likeCount + t.retweetCount, 0) / tweets.length;
      const timestamps = tweets.map(t => new Date(t.createdAt)).sort();

      // Get unique accounts for this token
      const uniqueAccounts = new Set(tweets.map(t => t.username)).size;

      const firstSeen = timestamps[0] ?? new Date(0);
      const lastSeen = timestamps[timestamps.length - 1] ?? new Date(0);
      mentions.push({
        symbol,
        count: tweets.length,
        tweets,
        sentiment: 'neutral', // Will be analyzed by SentimentAnalyzer
        sentimentScore: 0,    // Will be analyzed by SentimentAnalyzer
        avgEngagement: Math.round(engagement),
        firstSeen,
        lastSeen,
        uniqueAccounts,
        botScore: 0,          // Will be analyzed by BotDetector
        shillProbability: 0   // Will be analyzed by BotDetector
      });
    }

    return mentions.sort((a, b) => b.count - a.count);
  }

  private isMonetaryAmount(token: string): boolean {
    // Check against known monetary patterns
    for (const pattern of this.monetaryPatterns) {
      if (pattern.test(token)) {
        return true;
      }
    }

    // Additional checks for common monetary formats
    const tokenWithoutDollar = token.substring(1);

    // If it starts with a number, it's likely a monetary amount
    if (/^\d/.test(tokenWithoutDollar)) {
      return true;
    }

    // If it contains only numbers and common monetary suffixes, exclude it
    if (/^\d+[KMBT]?/.test(tokenWithoutDollar)) {
      return true;
    }

    // Common monetary abbreviations to exclude
    if (this.monetaryTerms.includes(tokenWithoutDollar)) {
      return true;
    }

    return false;
  }
}
